import { useEffect, useRef } from "react";
import type {
  FoaPart,
  ProcedureType,
  Regulation,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { earliestTenderDeadline } from "../../../../../common/procurement/earliestTenderDeadline";
import { minTenderDays } from "../utils/karens";
import { isDpsCompetition, isDpsScheme, isTwoStageProcedure } from "../utils/procedure";

// Margin over the statutory floor, so moving the announcement by a day does not invalidate it.
const MARGIN_DAYS = 7;

interface SuggestedBidDeadline {
  regulation: Regulation | null;
  foaPart: FoaPart | null;
  procedureType: ProcedureType | null;
  accelerated: boolean;
  announcementDate: string | null;
  bidSubmissionDeadline: string | null;
  isSaving: boolean;
  updateField: (fields: Partial<SourcingEventPatchDto>) => void;
}

export const useSuggestedBidDeadline = ({
  regulation,
  foaPart,
  procedureType,
  accelerated,
  announcementDate,
  bidSubmissionDeadline,
  isSaving,
  updateField,
}: SuggestedBidDeadline): void => {
  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current) return;
    if (isSaving) return;
    // Two-stage moves the tender period, not the deadline itself, and a DPS purchase is never
    // announced (§ 26-7) so there is no announcement to count from.
    if (isTwoStageProcedure(procedureType)) return;
    if (isDpsScheme(procedureType) || isDpsCompetition(procedureType)) return;
    // Below the EEA tier no fixed floor applies, so there is no minimum to add margin to.
    const minDays = minTenderDays(regulation, foaPart, procedureType, accelerated);
    if (minDays == null) return;
    if (announcementDate == null) return;
    if (bidSubmissionDeadline !== null) return;

    seededRef.current = true;
    updateField({
      eventData: {
        bidSubmissionDeadline: earliestTenderDeadline({
          minimumDays: minDays,
          announcementDate,
          now: new Date(),
          marginDays: MARGIN_DAYS,
        }),
      },
    });
  }, [
    regulation,
    foaPart,
    procedureType,
    accelerated,
    announcementDate,
    bidSubmissionDeadline,
    isSaving,
    updateField,
  ]);
};
